/**
 * POST /api/v1/uploads and GET /api/v1/uploads/{id}.
 *
 * Two things here are unusual and both are deliberate:
 *
 * 1. THE UPLOAD BYPASSES THE FETCH WRAPPER. `fetch` cannot report request-body
 *    progress — there is no upload-side event, and the streaming request bodies
 *    that would let you fake one are Chrome-only and require HTTP/2. A customer
 *    on a 3G connection pushing a 40 MB PDF needs a real percentage, not an
 *    indeterminate spinner for ninety seconds, so this one call is an
 *    XMLHttpRequest. Everything the wrapper does that matters is reimplemented
 *    below (credentials, x-request-id, bearer token, the `{error:{...}}`
 *    envelope, ApiError with the right `kind`) so callers cannot tell the
 *    difference from the outside. `apiUpload` is the ONLY place in the app that
 *    is allowed to do this.
 *
 * 2. THE RESPONSE IS A UNION. 201 gives the full report, 202 gives a poll
 *    target with a different shape. `isValidationPending` is the discriminator;
 *    the HTTP status is not needed to tell them apart, which is what lets the
 *    plain fetch wrapper handle the GET.
 */
import {
  ApiError,
  REQUEST_ID_HEADER,
  api,
  apiBaseUrl,
  getAccessToken,
  newRequestId,
} from './client';
import type { UploadResult, ValidationPending, ValidationReport } from './types';

export const UPLOADS_PATH = '/api/v1/uploads';

/** How long we let a single upload run before giving up. Uploads are slow; 3xx s. */
const UPLOAD_TIMEOUT_MS = 180_000;

/** Narrows the union. Keys off `status`, which only the 202 body carries. */
export function isValidationPending(result: UploadResult): result is ValidationPending {
  return (result as ValidationPending).status === 'validating';
}

/** The complement, so call sites read as prose rather than as `!isPending`. */
export function isValidationReport(result: UploadResult): result is ValidationReport {
  return !isValidationPending(result);
}

/** Seconds the server asked us to wait before polling again, with a sane floor. */
export function pollDelayMs(pending: ValidationPending): number {
  const seconds = pending.poll_after_seconds;
  const safe = typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : 2;
  // Floor of 1s so a server bug (or a 0) cannot turn this into a busy loop.
  return Math.max(1, Math.min(30, Math.round(safe))) * 1000;
}

/** GET /api/v1/uploads/{id}. Returns the report, or the pending body while it runs. */
export function fetchUploadReport(uploadId: string, signal?: AbortSignal): Promise<UploadResult> {
  return api.get<UploadResult>(`${UPLOADS_PATH}/${encodeURIComponent(uploadId)}`, {
    ...(signal ? { signal } : {}),
    // Polling must never see a cached body; the wrapper already sets no-store.
  });
}

export interface UploadProgress {
  /** Bytes pushed so far. */
  loaded: number;
  /** Total bytes, or 0 when the browser cannot tell us (rare, but possible). */
  total: number;
  /** 0-100, whole numbers. `undefined` when the total length is unknown. */
  percent: number | undefined;
}

export interface UploadPdfOptions {
  file: File;
  /** Called on every progress event. Throttling is the caller's business. */
  onProgress?: (progress: UploadProgress) => void;
  /** Abort the transfer. Aborting rejects with an ApiError of kind 'timeout'. */
  signal?: AbortSignal;
}

/** Parsed out of a failed XHR, mirroring client.ts's `errorFromBody`. */
function errorFromXhr(xhr: XMLHttpRequest, requestId: string): ApiError {
  const responseRequestId = xhr.getResponseHeader(REQUEST_ID_HEADER) ?? requestId;

  let parsed: unknown;
  try {
    parsed = xhr.responseText ? (JSON.parse(xhr.responseText) as unknown) : undefined;
  } catch {
    parsed = undefined;
  }

  const envelope = parsed as { error?: { code?: string; message?: string; request_id?: string } };
  if (envelope?.error && typeof envelope.error.message === 'string') {
    return new ApiError({
      code: typeof envelope.error.code === 'string' ? envelope.error.code : 'http_error',
      message: envelope.error.message,
      status: xhr.status,
      kind: 'http',
      requestId: envelope.error.request_id ?? responseRequestId,
    });
  }

  // No envelope. 413 is the one status worth its own sentence here, because a
  // reverse proxy — not the app — answers it and it has a concrete fix.
  const message =
    xhr.status === 413
      ? 'That file is larger than we can accept. Please upload a smaller PDF.'
      : xhr.status >= 500
        ? 'Something went wrong on our side. Please try uploading again.'
        : 'We could not accept that file. Please try again.';

  return new ApiError({
    code: xhr.status === 413 ? 'payload_too_large' : 'malformed_response',
    message,
    status: xhr.status,
    kind: xhr.status > 0 ? 'http' : 'network',
    requestId: responseRequestId,
  });
}

/**
 * POST /api/v1/uploads with real upload progress.
 *
 * Resolves with a ValidationReport (201) or a ValidationPending (202). Rejects
 * with an ApiError for everything else, including aborts, so callers have the
 * same one error shape as the rest of the app.
 */
export function apiUpload({ file, onProgress, signal }: UploadPdfOptions): Promise<UploadResult> {
  return new Promise<UploadResult>((resolve, reject) => {
    const requestId = newRequestId();
    const xhr = new XMLHttpRequest();
    const body = new FormData();
    body.append('file', file, file.name);

    let settled = false;
    let aborted = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      fn();
    };

    const onAbort = () => {
      aborted = true;
      xhr.abort();
    };

    xhr.open('POST', `${apiBaseUrl()}${UPLOADS_PATH}`, true);
    // The session cookie the API sets on this call is what lets an anonymous
    // customer keep their upload after they sign in, so credentials are not
    // optional here — this is the request that mints it.
    xhr.withCredentials = true;
    xhr.responseType = 'text';
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhr.setRequestHeader('accept', 'application/json');
    xhr.setRequestHeader(REQUEST_ID_HEADER, requestId);
    const token = getAccessToken();
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
    // Content-Type is deliberately NOT set: the browser must add the multipart
    // boundary itself, exactly as in the fetch wrapper.

    xhr.upload.addEventListener('progress', (event) => {
      const total = event.lengthComputable ? event.total : 0;
      onProgress?.({
        loaded: event.loaded,
        total,
        percent: total > 0 ? Math.min(100, Math.round((event.loaded / total) * 100)) : undefined,
      });
    });

    // The bytes are gone but the server is still validating. Report 100% so the
    // bar does not sit at 98% for the whole validation pass.
    xhr.upload.addEventListener('load', () => {
      onProgress?.({ loaded: file.size, total: file.size, percent: 100 });
    });

    xhr.addEventListener('load', () => {
      finish(() => {
        if (xhr.status === 201 || xhr.status === 202 || xhr.status === 200) {
          try {
            resolve(JSON.parse(xhr.responseText) as UploadResult);
          } catch (cause) {
            reject(
              new ApiError({
                code: 'malformed_response',
                message: 'We got an unexpected response from the server. Please try again.',
                status: xhr.status,
                kind: 'malformed',
                requestId,
                cause,
              }),
            );
          }
          return;
        }
        reject(errorFromXhr(xhr, requestId));
      });
    });

    xhr.addEventListener('error', () => {
      finish(() => {
        const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
        reject(
          new ApiError({
            code: offline ? 'offline' : 'network_error',
            message: offline
              ? "You're offline. Reconnect and upload again — nothing was lost."
              : "We couldn't reach the server. Please try uploading again.",
            status: 0,
            kind: offline ? 'offline' : 'network',
            requestId,
          }),
        );
      });
    });

    xhr.addEventListener('timeout', () => {
      finish(() => {
        reject(
          new ApiError({
            code: 'timeout',
            message: 'The upload took too long. Please check your connection and try again.',
            status: 0,
            kind: 'timeout',
            requestId,
          }),
        );
      });
    });

    xhr.addEventListener('abort', () => {
      finish(() => {
        reject(
          new ApiError({
            code: 'upload_cancelled',
            // Shown only if a caller forgets to swallow it; cancelling is not an error.
            message: 'Upload cancelled.',
            status: 0,
            kind: 'timeout',
            requestId,
            cause: aborted ? 'caller' : 'unknown',
          }),
        );
      });
    });

    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener('abort', onAbort, { once: true });

    xhr.send(body);
  });
}

/** True for the ApiError `apiUpload` rejects with when the caller aborted. */
export function isUploadCancelled(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'upload_cancelled';
}
